import { useCallback, useEffect, useRef, useState } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import IconButton from 'fe-design-base/molecules/IconButton';
import CircularInfinite from 'fe-design-base/molecules/Loaders/CircularInfinite';

import { getCoverRequestDetails } from 'features/notifications/api';
import { CollapsibleCard } from 'features/notifications/components/CollapsibleCard';
import { NoResponders } from 'features/notifications/components/NoResponders';
import { ShiftCard } from 'features/notifications/components/ShiftCard';
import { calculateScrollHeightWithRefs } from 'features/notifications/utils';

import { toI18n } from 'util/i18n';

import { CoverClaim } from './CoverShiftCard/CoverShiftCard';
import { CoverInvitedCard } from './CoverInvitedCard';
import { CoverShiftCard } from './CoverShiftCard';

type CoverDetailsProps = {
  requestId: string;
  isTeamRequest?: boolean;
  onClose: () => void;
};

const CoverDetails = ({
  requestId,
  isTeamRequest,
  onClose,
}: CoverDetailsProps) => {
  const [requestDetails, setRequestDetails] = useState<any>(null);
  const shiftId = requestId.replace('cover-', '');
  const headerRef = useRef<HTMLElement>(null);
  const shiftCardRef = useRef<HTMLElement>(null);
  const [scrollHeight, setScrollHeight] = useState('100vh');
  const [showResponders, setShowResponders] = useState(true);
  const [showInvited, setShowInvited] = useState(false);

  useEffect(() => {
    getCoverRequestDetails(shiftId).then(setRequestDetails);
  }, [shiftId]);

  const handleToggleResponders = useCallback(() => {
    setShowResponders(!showResponders);
  }, [showResponders]);

  const handleToggleInvited = useCallback(() => {
    setShowInvited(!showInvited);
  }, [showInvited]);

  useEffect(() => {
    const handleCalculateScrollHeight = () => {
      calculateScrollHeightWithRefs(headerRef, shiftCardRef, setScrollHeight);
    };

    handleCalculateScrollHeight();

    window.addEventListener('resize', handleCalculateScrollHeight);

    return () => {
      window.removeEventListener('resize', handleCalculateScrollHeight);
    };
  }, []);

  if (!requestDetails) {
    return (
      <Box column h="100vh" vcenter hcenter>
        <CircularInfinite size="large" variant="dark" />
      </Box>
    );
  }

  const { shift, owner, reason, requirement, claims, invited } = requestDetails;
  const location = {
    id: shift.location_id,
    name: shift.location_name,
    tz_name: shift.tz_name,
  };
  const { first_name, last_name } = owner;
  const name = `${first_name} ${last_name}`;

  return (
    <Box relative column h="100%">
      <Box
        ref={headerRef}
        flex
        row
        alignItemsCenter
        justifyContentSpaceBetween
        bbw={2}
        bcolor="mono300"
        p={16}
        ph={32}
      >
        <IconButton
          icon="ArrowLeft"
          label={toI18n('actions.go_back')}
          onClick={onClose}
        />
        <Text variant="bodyBold" i18n="requests_feed.cover.details.title" />
        <div style={{ width: '40px' }} />
      </Box>
      <Box ref={shiftCardRef}>
        <ShiftCard shift={shift} location={location} name={name} />
        {(requirement && requirement.length > 0) ||
          (reason && reason.length > 0 && (
            <Box btw={2} bcolor="mono300" p={16} ph={32}>
              {requirement && requirement.length > 0 && (
                <Box row gap={4} alignItemsCenter>
                  <Text
                    variant="bodySmBold"
                    color="mono700"
                    i18n="requests_feed.cover.details.qualifications"
                  />
                  <Text variant="bodySm" color="mono700">
                    {requirement}
                  </Text>
                </Box>
              )}
              {reason && reason.length > 0 && (
                <Box row gap={4} alignItemsCenter>
                  <Text
                    variant="bodySmBold"
                    color="mono700"
                    i18n="requests_feed.cover.details.reason"
                  />
                  <Text variant="bodySm" color="mono700">
                    {reason}
                  </Text>
                </Box>
              )}
            </Box>
          ))}
      </Box>
      <Box scrollY h={scrollHeight} grow>
        <Box flex column gap={8} p={16} bgcolor="mono100">
          <CollapsibleCard
            title="claims"
            count={claims?.length || 0}
            show={showResponders}
            handleToggle={handleToggleResponders}
          >
            <Box column gap={8}>
              {claims?.length > 0 &&
                claims.map((claim: CoverClaim) => (
                  <CoverShiftCard
                    key={claim.uuid}
                    claim={claim}
                    isTeamRequest={isTeamRequest}
                    location={location}
                  />
                ))}
              {claims && claims.length === 0 && <NoResponders type="cover" />}
            </Box>
          </CollapsibleCard>
          <CollapsibleCard
            title="invited"
            count={invited?.length || 0}
            show={showInvited}
            handleToggle={handleToggleInvited}
          >
            <Box column gap={8}>
              {invited.map((invitedUser: any) => (
                <CoverInvitedCard key={invitedUser.id} user={invitedUser} />
              ))}
            </Box>
          </CollapsibleCard>
        </Box>
      </Box>
    </Box>
  );
};

export default CoverDetails;
